/// <amd-module name="Artifacts/Package/components/package-details-common/PackageDependencies" />
import { PackageAttribute } from "Artifacts/Package/components/package-details-common/PackageAttribute";
import * as PackageRes from "Artifacts/Package/Resources";
import { DependencyHelpers } from "Artifacts/Root/helpers/DependencyHelpers";
import { IPackage, IPackageVersion } from "Artifacts/Root/types";
import { IDependencyGroup } from "Artifacts/Root/types/IDependency";
import { IFeed } from "Artifacts/Root/types/IFeed";
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IPackageDependenciesProps {
  pageContext: IVssPageContext;
  feed: IFeed;
  packageSummary: IPackage;
  packageVersion: IPackageVersion;
  dependencyHelperCallback?: (dg: IDependencyGroup[] | null) => void;
}

export interface IPackageDependenciesState {
  dependencyGroups: IDependencyGroup[];
  isSmartDependenciesEnabled: boolean;
}

export class PackageDependencies extends React.PureComponent<
  IPackageDependenciesProps,
  IPackageDependenciesState
> {
  private _mounted: boolean;
  constructor(props: IPackageDependenciesProps) {
    super(props);
    let depGroups = null;

    depGroups = this.props.packageVersion
      ? DependencyHelpers.groupDependencies(this.props.packageVersion.dependencies)
      : null;

    if (this.props.dependencyHelperCallback) {
      this.props.dependencyHelperCallback(depGroups);
    }

    const isSmartDependenciesEnabled = isFeatureFlagEnabled(
      this.props.pageContext,
      "Packaging.Feed.SmartDependencies",
      false,
    );

    // Load state with standard list of dependencies first
    this.state = {
      dependencyGroups: depGroups,
      isSmartDependenciesEnabled: isSmartDependenciesEnabled,
    } as IPackageDependenciesState;

    this._mounted = false;
  }

  public componentDidMount(): void {
    this._mounted = true;
    let depGroups = null;
    if (this.props.packageVersion.dependencies && this.state.isSmartDependenciesEnabled === true) {
      (async () => {
        const packageDependencies = await DependencyHelpers.getDependenciesForPackageVersionAsync(
          this.props.pageContext,
          this.props.feed.id,
          this.props.packageSummary.id,
          this.props.packageVersion.id,
          this.props.packageSummary.protocol,
          this.props.feed.project ? this.props.feed.project.name : undefined,
        );
        if (this._mounted === true && packageDependencies) {
          depGroups = DependencyHelpers.groupDependencyDetails(
            this.props.pageContext,
            packageDependencies,
            this.props.feed.name,
            this.props.packageSummary.protocol,
          );
          if (this.props.dependencyHelperCallback) {
            this.props.dependencyHelperCallback(depGroups);
          }
          this.setState({
            dependencyGroups: depGroups,
          });
        }
      })();
    }
  }

  public componentWillUnmount(): void {
    this._mounted = false;
  }

  public render(): JSX.Element {
    return (
      <div className="package-dependencies">
        <PackageAttribute title={PackageRes.PackageDependencies_Title}>
          {this.getDependencies()}
        </PackageAttribute>
      </div>
    );
  }

  getDependencies(): JSX.Element[] | undefined {
    if (!this.state.dependencyGroups || this.state.dependencyGroups.length < 1) {
      return undefined;
    }

    return this.state.dependencyGroups.map((dependencyGroup: IDependencyGroup) => {
      const dependencies = dependencyGroup.dependencies;

      if (dependencies && dependencies.length > 0) {
        return (
          <div key={"DependencyGroupSection_" + dependencyGroup.group}>
            <div
              className="package-group-header"
              key={dependencyGroup.group}
              role="heading"
              aria-level={3}
            >
              {dependencyGroup.group}
            </div>
            <ul>
              {dependencies.map((dependency) => {
                dependency.name = dependency.name || "";
                dependency.versionRange = dependency.versionRange || "";

                return (
                  <li
                    className="package-dependency"
                    key={dependency.name + dependency.versionRange}
                  >
                    {dependency.name + " " + dependency.versionRange}
                  </li>
                );
              })}
            </ul>
            <div className="package-group-spacer" />
          </div>
        );
      }
      return <div></div>;
    });
  }
}
